// Copyright (c) 2021 WiseTime. All rights reserved.

import { forkJoin, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, concatAll, filter, map, mergeMap } from "rxjs/operators"
import * as TimelineActions from "../actions"
import * as Slice from "../slices/stats"
import {
  ListDaySummariesRequest,
  ListDaySummariesResponse,
} from "../../../generated/wisetime/user/timeline/v4/timeline_pb"

import { grpc } from "../../../app/rpc/executor"
import { timelineClient } from "../../../app/rpc/client"
import { t } from "@lingui/macro"
import { splitDateRange } from "../dateTime"
import { showSnackBar } from "../../snackbar/actions"
import { DaySummariesResult } from "../slices/stats"

const splitIntoMultipleRequests = (action: { payload: ListDaySummariesRequest }): ListDaySummariesRequest[] => {
  return splitDateRange(Number(action.payload.getStartDate()), Number(action.payload.getEndDate()), 90)
    .map(dateRange => new ListDaySummariesRequest()
      .setStartDate(dateRange.startDateLocal.toString())
      .setEndDate(dateRange.endDateLocal.toString()))
}

type ListDaySummariesRpc = (request: ListDaySummariesRequest) => Observable<ListDaySummariesResponse>

export const listDaySummaries = (listDaySummariesRpc: ListDaySummariesRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.listDaySummaries.match),
      map(splitIntoMultipleRequests),
      mergeMap(requests => forkJoin(
        requests.map(request => of(request)
          .pipe(
            mergeMap(listDaySummariesRpc),
            map(t =>
              ({
                response: t.toObject(),
                range: {
                  startDateLocal: parseInt(request.getStartDate()),
                  endDateLocal: parseInt(request.getEndDate()),
                }
              } as DaySummariesResult)),
            map(Slice.setDaySummaries),
          ))
      ).pipe(
        concatAll(),
        catchError(_ => of(showSnackBar({
          message: t({
            id: "msg_get_day_summaries_failed",
            message: "Failed to get day summaries. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
          }),
          messageType: "ERROR",
        }))))
      )
    )

const listDaySummariesRpc = (request: ListDaySummariesRequest): Observable<ListDaySummariesResponse> =>
  grpc(() => timelineClient.listDaySummaries(request, null))

export default listDaySummaries(listDaySummariesRpc)
